import "./styles.css";
import { registerWebMcp } from "agents/experimental/webmcp";
import { COPY, type Locale } from "./i18n";
import { escapeHtml, renderMarkdown } from "./markdown";
import type { Article } from "./types";

interface PublicMedia {
  id: string;
  kind: "video" | "live";
  title: string;
  publicUrl: string;
  hlsUrl: string | null;
  isLive: boolean;
  isEnabled: boolean;
  createdAt: string;
}

const main = requiredElement<HTMLElement>("#main");
const searchPanel = requiredElement<HTMLElement>("#search-panel");
const searchToggle = requiredElement<HTMLButtonElement>("#search-toggle");
const searchForm = requiredElement<HTMLFormElement>("#search-form");
const searchResults = requiredElement<HTMLElement>("#search-results");
const editionDate = requiredElement<HTMLElement>("#edition-date");
const liveStatusButton = requiredElement<HTMLButtonElement>("#live-status");
const globalLivePlayer = requiredElement<HTMLElement>("#global-live-player");
const intentDialog = requiredElement<HTMLDialogElement>("#intent-dialog");
let locale = readLocale();
let copy = COPY[locale];
let activeLiveInput: PublicMedia | undefined;
let livePlayerExpanded = true;
let liveRefreshPending = false;
let hlsPlayer: import("hls.js").default | undefined;
let hlsModule: Promise<typeof import("hls.js")> | undefined;

applyLocale();
document.querySelectorAll<HTMLButtonElement>("[data-locale]").forEach((button) => {
  button.addEventListener("click", () => {
    const nextLocale = button.dataset.locale;
    if (nextLocale !== "es" && nextLocale !== "ca") return;
    locale = nextLocale;
    copy = COPY[locale];
    localStorage.setItem("dispatch-locale", locale);
    applyLocale();
    void routeSafely();
  });
});

searchToggle.addEventListener("click", () => {
  searchPanel.hidden = !searchPanel.hidden;
  if (!searchPanel.hidden) {
    document.querySelector<HTMLInputElement>("#search-input")?.focus();
  }
});

liveStatusButton.addEventListener("click", () => {
  if (!activeLiveInput) return;
  setLivePlayerExpanded(!livePlayerExpanded, true);
});

searchForm.addEventListener("submit", (event) => {
  event.preventDefault();
  const query = new FormData(searchForm).get("q")?.toString().trim();
  if (query) void runSearch(query);
});

document.addEventListener("click", (event) => {
  const target = event.target instanceof Element ? event.target.closest<HTMLElement>("[data-cta]") : null;
  if (!target?.dataset.cta) return;
  const body = JSON.stringify({
    ctaName: target.dataset.cta,
    articleId: target.dataset.articleId ?? null,
    path: `${window.location.pathname}${window.location.hash}`,
  });
  navigator.sendBeacon("/api/analytics/cta", new Blob([body], { type: "application/json" }));
});

document.addEventListener("click", (event) => {
  const target =
    event.target instanceof Element
      ? event.target.closest<HTMLElement>("[data-intent-help]")
      : null;
  if (!target) return;
  populateIntentDialog();
  intentDialog.showModal();
});

document.addEventListener("click", (event) => {
  if (!(event.target instanceof Element) || !event.target.closest("[data-live-collapse]")) return;
  setLivePlayerExpanded(false);
});

window.addEventListener("hashchange", () => void routeSafely());
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") void refreshLiveFeature();
});
window.setInterval(() => void refreshLiveFeature(), 5_000);
void setupWebMcp();
void routeSafely();

async function routeSafely(): Promise<void> {
  try {
    await renderRoute();
  } catch (error) {
    main.innerHTML = errorState(error);
  }
}

async function renderRoute(): Promise<void> {
  const pathMatch = window.location.pathname.match(/^\/articles\/([^/]+)$/);
  const hashMatch = window.location.hash.match(/^#\/article\/([^/]+)$/);
  const slug = pathMatch?.[1] ?? hashMatch?.[1];
  if (slug) {
    await renderArticle(decodeURIComponent(slug));
  } else {
    await renderHome();
  }
  main?.focus({ preventScroll: true });
}

async function renderHome(): Promise<void> {
  setLoading(copy.loadingToday);
  try {
    const [articleResponse, mediaResponse] = await Promise.all([
      fetch(`/api/articles?lang=${locale}`),
      fetch("/api/media"),
    ]);
    if (!articleResponse.ok) {
      throw new Error(copy.articlesFailed(articleResponse.status));
    }
    const { articles } = (await articleResponse.json()) as { articles: Article[] };
    const { media } = mediaResponse.ok
      ? ((await mediaResponse.json()) as { media: PublicMedia[] })
      : { media: [] };
    const live = media.find((item) => item.kind === "live" && item.isLive);
    const videos = media.filter((item) => item.kind === "video").slice(0, 3);
    updateGlobalLivePlayer(live);
    if (articles.length === 0) {
      main.innerHTML = emptyState(copy.noStories);
      return;
    }
    const [lead, ...rest] = articles;
    main.innerHTML = `
      <section class="edition-heading">
        <p class="eyebrow">${copy.todayEdition}</p>
        <p>${copy.editionDescription}</p>
      </section>
      <section class="lead-grid">
        ${articleVisual(lead, "lead-visual")}
        <article class="lead-story">
          ${tierBadge(lead)}
          ${agentPricing(lead)}
          <p class="section-label">${escapeHtml(lead.section)}</p>
          <h1><a href="/articles/${encodeURIComponent(lead.slug)}" data-cta="lead-headline" data-article-id="${escapeHtml(lead.id)}">${escapeHtml(lead.title)}</a></h1>
          <p class="lead-dek">${escapeHtml(lead.dek)}</p>
          <p class="byline">${copy.by} ${escapeHtml(lead.author)} · ${formatDate(lead.publishedAt)}</p>
          <a class="story-link" href="/articles/${encodeURIComponent(lead.slug)}" data-cta="read-more" data-article-id="${escapeHtml(lead.id)}">${copy.readMore} <span aria-hidden="true">→</span></a>
        </article>
      </section>
      <section class="story-grid" aria-label="${copy.moreReporting}">
        ${rest.map(articleCard).join("")}
      </section>
      ${videos.length ? videoFeature(videos) : ""}
      <section class="agent-callout">
        <p class="eyebrow">${copy.forAgents}</p>
        <h2>${copy.agentTitle}</h2>
        <p>${copy.agentDescription}</p>
        <div class="callout-actions">
          <a href="/.well-known/mcp.json?lang=${locale}" data-cta="mcp-discovery">${copy.discoverMcp}</a>
          <a href="/llms.txt?lang=${locale}" data-cta="agent-guide">${copy.agentGuide}</a>
        </div>
      </section>
    `;
  } catch (error) {
    main.innerHTML = errorState(error);
  }
}

async function renderArticle(slug: string): Promise<void> {
  setLoading(copy.openingStory);
  try {
    const response = await fetch(`/api/articles/${encodeURIComponent(slug)}?lang=${locale}`);
    if (response.status === 404) {
      main.innerHTML = emptyState(copy.storyNotFound);
      return;
    }
    if (!response.ok) throw new Error(copy.storyFailed(response.status));
    const article = (await response.json()) as Article;
    const granted = article.access === "granted";
    main.innerHTML = `
      <article class="article-page">
        <a class="back-link" href="#/">← ${copy.todayEdition}</a>
        <header class="article-header">
          ${tierBadge(article)}
          <p class="section-label">${escapeHtml(article.section)}</p>
          <h1>${escapeHtml(article.title)}</h1>
          <p class="article-dek">${escapeHtml(article.dek)}</p>
          <p class="byline">${copy.by} ${escapeHtml(article.author)} · ${formatDate(article.publishedAt)}</p>
        </header>
        ${articleVisual(article, "article-visual")}
        <div class="article-layout">
          <aside class="article-meta">
            <strong>${copy.usePolicy}</strong>
            <span>${copy.searchAllowed}</span>
            <span>${copy.aiInputAllowed}</span>
            <span>${copy.aiTrainingDenied}</span>
          </aside>
          <div class="article-body">
            ${granted && article.bodyMarkdown ? renderMarkdown(article.bodyMarkdown) : subscriptionGate(article)}
            ${granted ? advertisement() : ""}
            ${agentLicensePanel(article)}
          </div>
        </div>
      </article>
    `;
  } catch (error) {
    main.innerHTML = errorState(error);
  }
}

function liveFeature(live: PublicMedia): string {
  const hlsUrl = safeHttpUrl(live.hlsUrl);
  if (!hlsUrl) return "";
  return `
    <section class="live-feature">
      <div class="live-copy"><p class="eyebrow">${copy.liveEnabled}</p><h2>${escapeHtml(live.title)}</h2><p>${copy.liveDescription}</p><button type="button" data-live-collapse>${copy.hideLivePlayer}</button></div>
      <video data-live-video data-hls-url="${escapeHtml(hlsUrl)}" title="${copy.liveFrameTitle}: ${escapeHtml(live.title)}" crossorigin="anonymous" controls autoplay muted playsinline></video>
    </section>
  `;
}

function videoFeature(videos: PublicMedia[]): string {
  return `
    <section class="video-feature">
      <div class="edition-heading"><div><p class="eyebrow">${copy.watch}</p><h2>${copy.latestVideos}</h2></div><p>${copy.videoDescription}</p></div>
      <div class="video-grid">${videos
        .map((video) => {
          const url = safeHttpUrl(video.publicUrl);
          return url
            ? `<article><iframe src="${escapeHtml(url)}" title="${escapeHtml(video.title)}" loading="lazy" referrerpolicy="origin" allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe><h3>${escapeHtml(video.title)}</h3></article>`
            : "";
        })
        .join("")}</div>
    </section>
  `;
}

function updateGlobalLivePlayer(live: PublicMedia | undefined): void {
  const changedInput = live?.id !== activeLiveInput?.id;
  activeLiveInput = live;
  liveStatusButton.textContent = live ? copy.liveEnabled : copy.noLive;
  liveStatusButton.classList.toggle("is-live", Boolean(live));
  liveStatusButton.disabled = !live;
  if (!live) {
    destroyLivePlayer();
    globalLivePlayer.replaceChildren();
    globalLivePlayer.hidden = true;
    liveStatusButton.setAttribute("aria-expanded", "false");
    return;
  }
  if (changedInput) {
    destroyLivePlayer();
    livePlayerExpanded = true;
    globalLivePlayer.innerHTML = liveFeature(live);
    globalLivePlayer.dataset.liveId = live.id;
  }
  setLivePlayerExpanded(livePlayerExpanded);
}

function setLivePlayerExpanded(expanded: boolean, moveFocus = false): void {
  if (!activeLiveInput) return;
  livePlayerExpanded = expanded;
  globalLivePlayer.hidden = !expanded;
  if (expanded) void mountLivePlayer();
  else destroyLivePlayer();
  liveStatusButton.setAttribute("aria-expanded", String(expanded));
  liveStatusButton.setAttribute(
    "aria-label",
    expanded ? copy.hideLivePlayer : copy.showLivePlayer,
  );
  if (expanded && moveFocus) {
    globalLivePlayer.focus({ preventScroll: true });
    globalLivePlayer.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

async function mountLivePlayer(): Promise<void> {
  const video = globalLivePlayer.querySelector<HTMLVideoElement>("[data-live-video]");
  const source = safeHttpUrl(video?.dataset.hlsUrl ?? null);
  if (!video || !source || video.dataset.mounted === "true") return;
  video.dataset.mounted = "true";
  const canPlayNativeHls = Boolean(video.canPlayType("application/vnd.apple.mpegurl"));
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  if (canPlayNativeHls && isSafari) {
    video.src = source;
    await video.play().catch(() => undefined);
    return;
  }
  hlsModule ??= import("hls.js");
  const { default: Hls } = await hlsModule;
  if (!livePlayerExpanded || !globalLivePlayer.contains(video)) return;
  if (!Hls.isSupported()) {
    if (canPlayNativeHls) {
      video.src = source;
      await video.play().catch(() => undefined);
    }
    return;
  }
  hlsPlayer = new Hls({ lowLatencyMode: true, backBufferLength: 30 });
  hlsPlayer.loadSource(source);
  hlsPlayer.attachMedia(video);
  hlsPlayer.on(Hls.Events.MANIFEST_PARSED, () => {
    void video.play().catch(() => undefined);
  });
  hlsPlayer.on(Hls.Events.ERROR, (_event, data) => {
    if (!data.fatal || !hlsPlayer) return;
    if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hlsPlayer.startLoad();
    else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hlsPlayer.recoverMediaError();
    else destroyLivePlayer();
  });
}

function destroyLivePlayer(): void {
  hlsPlayer?.destroy();
  hlsPlayer = undefined;
  const video = globalLivePlayer.querySelector<HTMLVideoElement>("[data-live-video]");
  if (!video) return;
  video.pause();
  video.removeAttribute("src");
  video.removeAttribute("data-mounted");
  video.load();
}

async function refreshLiveFeature(): Promise<void> {
  if (liveRefreshPending) return;
  liveRefreshPending = true;
  try {
    const response = await fetch("/api/media", { cache: "no-store" });
    if (!response.ok) return;
    const { media } = (await response.json()) as { media: PublicMedia[] };
    const live = media.find((item) => item.kind === "live" && item.isLive);
    updateGlobalLivePlayer(live);
  } catch {
    return;
  } finally {
    liveRefreshPending = false;
  }
}

function articleCard(article: Article): string {
  return `
    <article class="story-card">
      ${articleVisual(article, "card-visual")}
      <div class="card-copy">
        ${tierBadge(article)}
        ${agentPricing(article)}
        <p class="section-label">${escapeHtml(article.section)}</p>
        <h2><a href="/articles/${encodeURIComponent(article.slug)}" data-cta="card-headline" data-article-id="${escapeHtml(article.id)}">${escapeHtml(article.title)}</a></h2>
        <p>${escapeHtml(article.dek)}</p>
        <a class="story-link" href="/articles/${encodeURIComponent(article.slug)}" data-cta="read-more" data-article-id="${escapeHtml(article.id)}">${copy.readMore} <span aria-hidden="true">→</span></a>
      </div>
    </article>
  `;
}

function articleVisual(article: Article, className: string): string {
  const image = safeHttpUrl(article.heroImageUrl);
  if (image) {
    return `<figure class="story-visual-frame"><img class="story-visual ${className}" src="${escapeHtml(image)}" alt="" loading="lazy">${article.heroImageProvenance === "workers_ai" ? `<figcaption>${copy.aiGeneratedImage}</figcaption>` : ""}</figure>`;
  }
  return `<div class="story-visual visual-placeholder ${className}" role="img" aria-label="${copy.illustrationFor} ${escapeHtml(article.title)}"><span>${escapeHtml(article.section)}</span></div>`;
}

function subscriptionGate(article: Article): string {
  return `
    <div class="teaser-copy">${renderMarkdown(article.teaserMarkdown)}</div>
    <section class="subscription-card">
      <p class="eyebrow">${copy.subscriptionIncluded}</p>
      <h2>${copy.subscriptionTitle}</h2>
      <p>${copy.subscriptionDescription}</p>
      <button class="primary-cta" type="button" data-cta="subscribe" data-article-id="${escapeHtml(article.id)}">${copy.subscriptionButton}</button>
    </section>
  `;
}

function advertisement(): string {
  return `<aside class="ad-slot"><span>${copy.advertisement}</span><p>${copy.advertisementDescription}</p></aside>`;
}

function agentPricing(article: Article): string {
  return `
    <div class="agent-price-row">
      <span>${copy.agentSearch} · ${formatUsd(article.agentSearchPriceCents)}</span>
      <span>${copy.aiTraining} · ${formatUsd(article.trainingPriceCents)}</span>
      <button type="button" data-intent-help data-cta="intent-help" data-article-id="${escapeHtml(article.id)}" aria-label="${copy.intentHelpLabel}">?</button>
    </div>
  `;
}

function agentLicensePanel(article: Article): string {
  return `
    <section class="agent-license-card">
      <div class="agent-license-heading">
        <div><p class="eyebrow">${copy.agentLicenses}</p><h2>${copy.agentLicenseDescription}</h2></div>
        <button type="button" data-intent-help data-cta="intent-help" data-article-id="${escapeHtml(article.id)}" aria-label="${copy.intentHelpLabel}">?</button>
      </div>
      <div class="license-options">
        ${licenseOption(copy.agentSearch, article.agentSearchPriceCents, article.agentSearchRequest.url, "Accept: text/markdown")}
        ${licenseOption(copy.aiTraining, article.trainingPriceCents, article.trainingRequest.url, "Accept: text/markdown · X-License-Intent: ai_training")}
      </div>
    </section>
  `;
}

function licenseOption(
  title: string,
  priceCents: number,
  canonicalUrl: string,
  requestHeaders: string,
): string {
  return `
    <article>
      <span>${title}</span>
      <strong>${formatUsd(priceCents)}</strong>
      <code>${escapeHtml(canonicalUrl)}</code>
      <small>${escapeHtml(requestHeaders)}</small>
    </article>
  `;
}

function tierBadge(article: Article): string {
  return article.humanAccess === "open"
    ? `<span class="tier-badge tier-free">${copy.openArticle} · ${copy.adSupported}</span>`
    : `<span class="tier-badge tier-premium">${copy.premiumArticle} · ${copy.subscriptionIncluded}</span>`;
}

function formatUsd(cents: number): string {
  return new Intl.NumberFormat(copy.intlLocale, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

function populateIntentDialog(): void {
  requiredElement<HTMLElement>("#intent-title").textContent = copy.intentTitle;
  requiredElement<HTMLElement>("#intent-description").textContent = copy.intentDescription;
  requiredElement<HTMLElement>("#intent-search-title").textContent = copy.agentSearch;
  requiredElement<HTMLElement>("#intent-search-description").textContent =
    copy.searchIntentExplanation;
  requiredElement<HTMLElement>("#intent-training-title").textContent = copy.aiTraining;
  requiredElement<HTMLElement>("#intent-training-description").textContent =
    copy.trainingIntentExplanation;
  requiredElement<HTMLElement>("#intent-limit").textContent = copy.intentLimit;
  requiredElement<HTMLButtonElement>("#intent-close").textContent = copy.close;
}

async function runSearch(query: string): Promise<void> {
  searchResults.innerHTML = `<p class="search-feedback">${copy.searching}</p>`;
  try {
    const response = await fetch(
      `/api/search?q=${encodeURIComponent(query)}&lang=${locale}`,
    );
    if (!response.ok) throw new Error(copy.searchFailed(response.status));
    const { articles } = (await response.json()) as { articles: Article[] };
    searchResults.innerHTML = articles.length
      ? `<div class="search-result-list">${articles
          .map(
            (article) => `<a href="/articles/${encodeURIComponent(article.slug)}" data-cta="read-more" data-article-id="${escapeHtml(article.id)}"><strong>${escapeHtml(article.title)}</strong><span>${escapeHtml(article.dek)}</span></a>`,
          )
          .join("")}</div>`
      : `<p class="search-feedback">${copy.noResults}</p>`;
  } catch (error) {
    searchResults.innerHTML = `<p class="search-feedback">${escapeHtml(errorMessage(error))}</p>`;
  }
}

function safeHttpUrl(value: string | null): string | null {
  if (!value) return null;
  try {
    const url = new URL(value, window.location.origin);
    return url.protocol === "https:" || url.origin === window.location.origin
      ? url.toString()
      : null;
  } catch {
    return null;
  }
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat(copy.intlLocale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(value));
}

function setLoading(message: string): void {
  main.innerHTML = `<section class="loading-state"><p class="eyebrow">Rambla Oberta</p><h1>${escapeHtml(message)}</h1></section>`;
}

function emptyState(message: string): string {
  return `<section class="empty-state"><h1>${escapeHtml(message)}</h1><a href="#/">${copy.returnHome}</a></section>`;
}

function errorState(error: unknown): string {
  return `<section class="empty-state"><p class="eyebrow">${copy.unableToLoad}</p><h1>${escapeHtml(errorMessage(error))}</h1><a class="primary-cta" href="/">${copy.retry}</a></section>`;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : copy.unexpectedError;
}

function readLocale(): Locale {
  const requested = new URL(window.location.href).searchParams.get("lang");
  if (requested === "es" || requested === "ca") return requested;
  return localStorage.getItem("dispatch-locale") === "ca" ? "ca" : "es";
}

function applyLocale(): void {
  document.documentElement.lang = copy.htmlLang;
  document.querySelectorAll<HTMLElement>("[data-copy]").forEach((element) => {
    const key = element.dataset.copy;
    if (key && copy.staticText[key]) element.textContent = copy.staticText[key];
  });
  document.querySelectorAll<HTMLElement>("[data-copy-aria]").forEach((element) => {
    const key = element.dataset.copyAria;
    if (key && copy.staticText[key]) element.setAttribute("aria-label", copy.staticText[key]);
  });
  document.querySelectorAll<HTMLInputElement>("[data-copy-placeholder]").forEach((element) => {
    const key = element.dataset.copyPlaceholder;
    if (key && copy.staticText[key]) element.placeholder = copy.staticText[key];
  });
  document.querySelector<HTMLMetaElement>('meta[name="description"]')?.setAttribute(
    "content",
    copy.staticText.footerTagline,
  );
  editionDate.textContent = `Barcelona · ${new Intl.DateTimeFormat(copy.intlLocale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date())}`;
  document.querySelectorAll<HTMLButtonElement>("[data-locale]").forEach((button) => {
    button.setAttribute("aria-pressed", String(button.dataset.locale === locale));
  });
  if (intentDialog.open) populateIntentDialog();
  if (activeLiveInput) {
    destroyLivePlayer();
    globalLivePlayer.innerHTML = liveFeature(activeLiveInput);
  }
  updateGlobalLivePlayer(activeLiveInput);
  const url = new URL(window.location.href);
  if (locale === "ca") url.searchParams.set("lang", "ca");
  else url.searchParams.delete("lang");
  window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
  document.querySelectorAll<HTMLAnchorElement>('a[href^="/llms.txt"]').forEach((link) => {
    link.href = `/llms.txt?lang=${locale}`;
  });
  document.querySelectorAll<HTMLAnchorElement>('a[href^="/.well-known/mcp.json"]').forEach((link) => {
    link.href = `/.well-known/mcp.json?lang=${locale}`;
  });
}

async function setupWebMcp(): Promise<void> {
  if (!("modelContext" in navigator)) return;
  try {
    const handle = await registerWebMcp({
      url: "/mcp",
      prefix: "news.",
      watch: false,
      onSync: (tools) => {
        document.documentElement.dataset.webmcp = "ready";
        document.documentElement.dataset.webmcpTools = String(tools.length);
      },
      onError: (error) => console.warn({ event: "webmcp_error", message: error.message }),
    });
    window.addEventListener(
      "pagehide",
      () => void handle.dispose().catch((error) => console.warn({ event: "webmcp_dispose_failed", message: errorMessage(error) })),
      { once: true },
    );
  } catch (error) {
    console.warn({ event: "webmcp_setup_failed", message: errorMessage(error) });
  }
}

function requiredElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Falta un elemento requerido: ${selector}`);
  return element;
}
